import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useModal } from '../../hooks/useModal';
import Button from '../../components/ui/Button';
import InfoModal from '../../components/ui/InfoModal';
import NavigationTabs from '../../components/admin/NavigationTabs';
import AdminHeader from '../../components/admin/AdminHeader';
import feedbackService from '../../utils/feedbackService';
import adminModerationService from '../../services/adminModerationService';
import jobModerationService from '../../services/jobModerationService';
import { profilePictureService } from '../../utils/profilePictureService';

const FeedbackDashboard = () => {
  const { userProfile } = useAuth();
  const { infoModal, showInfo, closeInfo } = useModal();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Get active tab from URL parameters, default to 'feedback' since this is the feedback page
  const [activeTab, setActiveTab] = useState('feedback');

  const [feedback, setFeedback] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: '',
    type: '',
    rating: ''
  });
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [adminResponse, setAdminResponse] = useState('');
  const [isResponding, setIsResponding] = useState(false);

  // Dummy data for navigation tabs (these would come from API calls)
  const [reports, setReports] = useState([]);
  const [users, setUsers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [pendingPictureReviews, setPendingPictureReviews] = useState(0);
  const [pendingJobModerations, setPendingJobModerations] = useState(0);

  // Since this is the feedback page, we don't need URL parameter handling for tabs
  // The active tab is always 'feedback'

  useEffect(() => {
    loadFeedback();
    loadStats();
    loadNavigationData();
  }, [filters]);

  // Load data for navigation tabs
  const loadNavigationData = async () => {
    try {
      const [reportsResponse, usersResponse, jobsResponse, reviewsResponse, pendingModerationsResponse] = await Promise.all([
        adminModerationService.getReports(),
        adminModerationService.getUsers(),
        adminModerationService.getJobs(),
        profilePictureService.getPendingReviews(),
        jobModerationService.getPendingJobModerationsCount()
      ]);

      if (reportsResponse.success) setReports(reportsResponse.data);
      if (usersResponse.success) setUsers(usersResponse.data);
      if (jobsResponse.success) setJobs(jobsResponse.data);
      if (reviewsResponse.success) setPendingPictureReviews(reviewsResponse.data.length);
      if (pendingModerationsResponse.success) setPendingJobModerations(pendingModerationsResponse.count);
    } catch (error) {
      console.error('Error loading navigation data:', error);
    }
  };

  const loadFeedback = async () => {
    setLoading(true);
    try {
      const result = await feedbackService.getAllFeedback(filters);
      if (result.success) {
        setFeedback(result.data);
      } else {
        showInfo({
          title: 'Error',
          message: 'Failed to load feedback data',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error loading feedback:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const result = await feedbackService.getFeedbackStats();
      if (result.success) {
        setStats(result.data);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const handleStatusUpdate = async (feedbackId, newStatus) => {
    try {
      const result = await feedbackService.updateFeedbackStatus(
        feedbackId, 
        newStatus, 
        adminResponse || null, 
        userProfile.id
      );
      
      if (result.success) {
        loadFeedback();
        setSelectedFeedback(null);
        setAdminResponse('');
        showInfo({
          title: 'Success',
          message: 'Feedback updated successfully',
          type: 'success'
        });
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('Error updating feedback:', error);
      showInfo({
        title: 'Error',
        message: 'Failed to update feedback',
        type: 'error'
      });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'reviewed': return 'bg-blue-100 text-blue-800';
      case 'resolved': return 'bg-green-100 text-green-800';
      case 'closed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getRatingStars = (rating) => {
    if (!rating) return 'No rating';
    return '⭐'.repeat(rating) + '☆'.repeat(5 - rating);
  };

  if (loading && !stats) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading feedback dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">

      {/* Header */}
      <div className=" shadow-sm border-b border-gray-200">
        <div className=" mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Feedback Dashboard</h1>
              <p className="text-gray-600">Manage and respond to user feedback</p>
            </div>
            <Button onClick={() => window.location.reload()}>
              🔄 Refresh
            </Button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="text-2xl mr-3">💬</div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
                  <p className="text-sm text-gray-600">Total Feedback</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="text-2xl mr-3">⭐</div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{stats.averageRating}</p>
                  <p className="text-sm text-gray-600">Average Rating</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="text-2xl mr-3">📈</div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{stats.recentCount}</p>
                  <p className="text-sm text-gray-600">Last 7 Days</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="text-2xl mr-3">⏳</div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{stats.byStatus.pending || 0}</p>
                  <p className="text-sm text-gray-600">Pending Review</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className=" mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow mb-6 p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="reviewed">Reviewed</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select
                value={filters.type}
                onChange={(e) => setFilters({ ...filters, type: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Types</option>
                <option value="general">General</option>
                <option value="job_application">Job Application</option>
                <option value="interview">Interview</option>
                <option value="employer_interaction">Employer Interaction</option>
                <option value="feature_request">Feature Request</option>
                <option value="bug_report">Bug Report</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Rating</label>
              <select
                value={filters.rating}
                onChange={(e) => setFilters({ ...filters, rating: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Ratings</option>
                <option value="5">5 Stars</option>
                <option value="4">4 Stars</option>
                <option value="3">3 Stars</option>
                <option value="2">2 Stars</option>
                <option value="1">1 Star</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Feedback List */}
      <div className=" mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading feedback...</p>
            </div>
          ) : feedback.length === 0 ? (
            <div className="p-8 text-center">
              <div className="text-4xl mb-4">📭</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Feedback Found</h3>
              <p className="text-gray-600">Try adjusting your filters or check back later.</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {feedback.map((item) => (
                <div key={item.id} className="p-6 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(item.status)}`}>
                          {item.status}
                        </span>
                        <span className="text-sm text-gray-600 font-medium">
                          {item.feedback_type.replace('_', ' ')}
                        </span>
                        {item.rating && (
                          <span className="text-sm text-gray-600">
                            {getRatingStars(item.rating)}
                          </span>
                        )}
                      </div>
                      
                      <div className="mb-2">
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">{item.user?.full_name}</span>
                          {' '}({item.user?.email})
                        </p>
                        <p className="text-xs text-gray-500">
                          {new Date(item.created_at).toLocaleDateString()} at {new Date(item.created_at).toLocaleTimeString()}
                        </p>
                      </div>
                      
                      <p className="text-gray-900 mb-3">{item.comment}</p>
                      
                      {item.admin_response && (
                        <div className="bg-blue-50 border-l-4 border-blue-400 p-3 mb-3">
                          <div className="flex items-center mb-1">
                            <span className="text-sm font-medium text-blue-800">Admin Response</span>
                            {item.admin && (
                              <span className="text-xs text-blue-600 ml-2">
                                by {item.admin.full_name}
                              </span>
                            )}
                          </div>
                          <p className="text-blue-700 text-sm">{item.admin_response}</p>
                          {item.responded_at && (
                            <p className="text-xs text-blue-600 mt-1">
                              {new Date(item.responded_at).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                    
                    <div className="flex flex-col items-end space-y-3 ml-4">
                      {item.rating && (
                        <div className="text-center">
                          <div className="text-2xl font-bold text-yellow-500">{item.rating}</div>
                          <div className="text-xs text-yellow-600">{getRatingStars(item.rating)}</div>
                        </div>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedFeedback(item)}
                      >
                        {item.status === 'pending' ? 'Respond' : 'Update'}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Response Modal */}
      {selectedFeedback && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">
                Manage Feedback
              </h2>
              <button
                onClick={() => {
                  setSelectedFeedback(null);
                  setAdminResponse('');
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                ✕
              </button>
            </div>

            <div className="mb-6">
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <div className="flex items-center space-x-2 mb-2">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(selectedFeedback.status)}`}>
                    {selectedFeedback.status}
                  </span>
                  <span className="text-sm text-gray-600 font-medium">
                    {selectedFeedback.feedback_type.replace('_', ' ')}
                  </span>
                  {selectedFeedback.rating && (
                    <span className="text-sm text-gray-600">
                      {getRatingStars(selectedFeedback.rating)}
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-600 mb-2">
                  <span className="font-medium">{selectedFeedback.user?.full_name}</span>
                  {' '}({selectedFeedback.user?.email})
                </p>
                <p className="text-gray-900">{selectedFeedback.comment}</p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Admin Response (Optional)
                </label>
                <textarea
                  value={adminResponse}
                  onChange={(e) => setAdminResponse(e.target.value)}
                  placeholder="Provide a response to the user (optional)..."
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                />
              </div>

              <div className="flex items-center space-x-3">
                <Button
                  onClick={() => handleStatusUpdate(selectedFeedback.id, 'reviewed')}
                  disabled={isResponding}
                  variant="outline"
                >
                  Mark as Reviewed
                </Button>
                <Button
                  onClick={() => handleStatusUpdate(selectedFeedback.id, 'resolved')}
                  disabled={isResponding}
                >
                  Mark as Resolved
                </Button>
                <Button
                  onClick={() => handleStatusUpdate(selectedFeedback.id, 'closed')}
                  disabled={isResponding}
                  variant="outline"
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Info Modal */}
      {infoModal && (
        <InfoModal
          title={infoModal.title}
          message={infoModal.message}
          type={infoModal.type}
          onClose={closeInfo}
        />
      )}
    </div>
  );
};

export default FeedbackDashboard;